import { useState, useRef, useEffect } from 'react';
import type { ChangeEvent } from 'react';
import { SEARCH_DEBOUNCE_MS } from '../utils/constants/admin';

export interface UseDebouncedSearchReturn {
  searchInput: string;
  handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * Drops any pending debounced call and resets the input to empty — for a
   * "Clear filters" action that resets the search's source itself. Clearing
   * the input here (rather than relying on the `syncedValue` effect) matters
   * when the synced value was already empty, so that effect wouldn't rerun.
   */
  resetSearch: () => void;
}

/**
 * `syncedValue`, when given, is the search's source of truth (e.g. a URL
 * param): the input starts from it and follows it whenever it changes from
 * outside — a "Clear filters", a nav link to the bare route — but not while a
 * keystroke is still waiting to be applied, which would clobber the typing.
 */
export const useDebouncedSearch = (
  onSearch: (val: string) => void,
  delay = SEARCH_DEBOUNCE_MS,
  syncedValue?: string,
): UseDebouncedSearchReturn => {
  const [searchInput, setSearchInput] = useState(syncedValue ?? '');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelPendingSearch = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = null;
  };

  const resetSearch = () => {
    cancelPendingSearch();
    setSearchInput('');
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchInput(val);
    cancelPendingSearch();
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      onSearch(val);
    }, delay);
  };

  useEffect(() => {
    if (syncedValue !== undefined && debounceRef.current === null) setSearchInput(syncedValue);
  }, [syncedValue]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return { searchInput, handleSearchChange, resetSearch };
};
